import React, { useState } from 'react'
import {
  Box,
  Button,
  Heading,
  Typography,
  Frame,
  SelectField,
  Chip
} from 'components'
import { ProgressBarFooter } from './FreelancerProgressBarFooter'
import { usePrograms, updateUser, useContent, useHistory } from 'utils'
import firebase from 'utils/firebase'


const FreelancerProgramSelect = ({
  onboarding,
  user,
  stepIndex,
  totalSteps,
  onNext = () => { },
  onSubmit = () => { }
}) => {
  const [selectedProgram, setSelectedProgram] = useState('')
  const [selectedPrograms, setSelectedPrograms] = useState(user?.programs || [])
  const programList = usePrograms()
  const { landing } = useContent() || {}
  const history = useHistory()
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'

  // Add console logs to debug
  // console.log('programList:', programList);

  if (!programList) {
    return (
      <Frame maxWidth="xs" user={user}>
        <Box mx="auto" maxWidth={430} display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography>Loading programs...</Typography>
        </Box>
      </Frame>
    )
  }

  const handleAddProgram = () => {
    if (selectedProgram && !selectedPrograms.some(p => p.id === selectedProgram)) {
      const selectedProg = programList.find(prog => prog.id === selectedProgram);
      if (selectedProg) {
        setSelectedPrograms([...selectedPrograms, selectedProg])
        setSelectedProgram('')
      }
    }
  }

  const handleRemoveProgram = (programId) => {
    setSelectedPrograms(selectedPrograms.filter(p => p.id !== programId))
  }

  const handleSubmit = () => {
    // It's okay to continue even with no programs selected
    if (onboarding) {
      firebase.analytics().logEvent('user_freelancer_program_select_submit_click')
    }

    // console.log('Updating user with programs:', selectedPrograms);

    // Create a clean array of program objects for storage
    const cleanPrograms = selectedPrograms.map(program => ({
      id: program.id,
      name: program.name
    }));

    // console.log('Cleaned programs for storage:', cleanPrograms);

    // Use direct Firebase update with set and merge to ensure data is saved properly
    firebase
      .firestore()
      .collection('users')
      .doc(user.id)
      .set({
        programs: cleanPrograms
      }, { merge: true })
      .then(() => {
        // console.log('Programs successfully updated in database')
        if (onSubmit) {
          onSubmit({ programs: cleanPrograms })
        }

        if (onboarding && onNext) {
          // If in onboarding mode, call onNext
          onNext()
        } else if (!onboarding) {
          // If not in onboarding mode, redirect back to profile
          history.push('/profile')
        }
      })
      .catch(error => {
        console.error('Error updating programs:', error)
      })
  }

  return (
    <Frame maxWidth="xs" user={user}>
      <Box mx="auto" maxWidth={430}>
        <Heading>
          {onboarding
            ? 'Are you a graduate of any BIPOC TV & Film programs?'
            : 'Manage Your Program Affiliations'
          }
        </Heading>

        <Box display="flex" flexDirection="column">
          <Box mb={3}>
            <SelectField
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
              label="Select a program"
              choices={programList.map(program => ({
                value: program.id,
                label: program.name
              }))}
            />
          </Box>

          <Box mb={3}>
            <Button
              variant="contained"
              style={{ 'background-color': buttonColor }}
              onClick={handleAddProgram}
              disabled={!selectedProgram}
              fullWidth
            >
              Add Program
            </Button>
          </Box>

          {selectedPrograms.length > 0 && (
            <Box mt={3}>
              <Typography variant="h6" style={{ fontWeight: 500, marginBottom: '12px' }}>
                Selected Programs
              </Typography>
              <Box>
                {selectedPrograms.map((program) => (
                  <Chip
                    key={program.id}
                    label={program.name}
                    onDelete={() => handleRemoveProgram(program.id)}
                    color="primary"
                    style={{ margin: '4px' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {onboarding && (
        <ProgressBarFooter
          valid={true} // Always valid, even with no selections
          stepIndex={stepIndex}
          firstStep={stepIndex}
          numSteps={totalSteps}
          onNext={handleSubmit}
          onPrev={() => { }}
        />
      )}

      {!onboarding && (
        <Box mx="auto" my={3} maxWidth={380} display="flex" justifyContent="center" flexWrap="wrap" style={{ gap: '1rem' }}>
          <Button
            variant="contained"
            style={{ backgroundColor: buttonColor, color: 'white', minWidth: 180 }}
            onClick={handleSubmit}
          >
            Save Programs
          </Button>

          <Button
            variant="contained"
            style={{ backgroundColor: buttonColor, color: 'white', minWidth: 180 }}
            onClick={() => history.push('/profile')}
          >
            Back to Profile
          </Button>
        </Box>
      )}
    </Frame>
  )
}

export default FreelancerProgramSelect
